/**
 * One board, whether a piece of it is ink or a shape.
 *
 * The eraser and the select tool were built to work on strokes, and a shape is a list
 * of points with a colour and a width — so it is handed to them as one. This file is
 * the only place that knows which is which: it reads the id, and sends the work to
 * the ink or to the objects accordingly.
 *
 * That is what makes shapes movable, resizable, turnable, recolourable and erasable
 * without a single change to the tools that do those things.
 */

import type { BoardObject, Rect, Stroke } from '@opal/shared';

import type { ErasableInk } from './erase-session.js';
import type { InkSession } from './ink-session.js';
import type { ObjectBoard } from './object-board.js';
import { duplicateObject, isCuttable, objectFromStroke, objectStroke } from './object-view.js';
import type { SelectableInk } from './select-session.js';
import { duplicateStroke, type Copied, type Duplicated } from './selection.js';
import { isObjectId } from './shape-object.js';
import { reordered, type ZMove } from './z-order.js';

/** Everything on the page, as the tools that change it need to see it. */
export type BoardItems = SelectableInk & ErasableInk;

/**
 * §16 — the clipboard's view of the page.
 *
 * The record itself, not the stroke-shaped view of it, so a sticky note pasted an
 * hour later is still a sticky note with its words in it.
 */
function clipboardItems(
  ink: InkSession,
  objects: ObjectBoard,
): Pick<BoardItems, 'copyOf' | 'place' | 'all'> {
  return {
    copyOf(strokeId: string): Copied | null {
      if (!isObjectId(strokeId)) {
        const stroke = ink.find(strokeId);
        return stroke ? { stroke, object: null } : null;
      }
      const object = objects.find(strokeId);
      return object ? { stroke: null, object } : null;
    },

    /** A held copy put back on the board, offset, as a new piece of work. */
    place(item: Copied, offset: number): Duplicated | null {
      if (item.stroke) {
        const copy = duplicateStroke(item.stroke, offset);
        ink.accept(copy);
        return { view: copy, stroke: copy, object: null };
      }
      if (!item.object) return null;
      const copy = duplicateObject(item.object, offset);
      objects.put(copy);
      return { view: objectStroke(copy), stroke: null, object: copy };
    },

    /** Everything on the page, ink and objects alike, for Ctrl+A. */
    all(): readonly Stroke[] {
      return [...ink.strokes, ...objects.objects.map(objectStroke)];
    },
  };
}

export function createBoardItems(ink: InkSession, objects: ObjectBoard): BoardItems {
  return {
    near(area: Rect): readonly Stroke[] {
      // Shapes are painted above the ink, so they come last: a click where a shape
      // lies over a line takes the shape, which is the one the teacher can see.
      return [...ink.near(area), ...objects.strokesNear(area)];
    },

    find(strokeId: string): Stroke | null {
      if (!isObjectId(strokeId)) return ink.find(strokeId);
      const object = objects.find(strokeId);
      return object ? objectStroke(object) : null;
    },

    remove(strokeId: string): boolean {
      return isObjectId(strokeId) ? objects.remove(strokeId) : ink.remove(strokeId);
    },

    accept(stroke: Stroke): void {
      // Only ink arrives here: what survives when the eraser cuts through a shape is
      // ink from that moment on. A copy of an object goes through `duplicate`.
      ink.accept(stroke);
    },

    replace(stroke: Stroke): boolean {
      if (!isObjectId(stroke.id)) return ink.replace(stroke);
      const object = objects.find(stroke.id);
      if (!object) return false;
      objects.put(objectFromStroke(object, stroke));
      return true;
    },

    /**
     * SRS C4 — a copy of a piece of ink is ink, and a copy of a sticky note is a
     * sticky note. A note copied as ink would be an empty square with no words in it.
     */
    duplicate(stroke: Stroke, offset: number): Duplicated | null {
      if (!isObjectId(stroke.id)) return ink.duplicate(stroke, offset);
      const object = objects.find(stroke.id);
      if (!object) return null;
      const copy = duplicateObject(object, offset);
      objects.put(copy);
      return { view: objectStroke(copy), stroke: null, object: copy };
    },

    ...clipboardItems(ink, objects),

    /** SRS C4 — a text box and a sticky note come off whole, however the eraser is set. */
    wholeOnly(strokeId: string): boolean {
      if (!isObjectId(strokeId)) return false;
      const object = objects.find(strokeId);
      return object ? !isCuttable(object) : false;
    },

    /**
     * SRS C8 — sends the chosen objects forward or back within their own layer.
     *
     * Ink has no z-order of its own: a line is drawn over what was there before it,
     * and that is the whole of it. So only the objects among a selection move, and
     * the strokes are quietly left alone.
     */
    reorder(strokeIds: readonly string[], move: ZMove): readonly BoardObject[] {
      const moved = reordered(objects.objects, strokeIds.filter(isObjectId), move);
      for (const object of moved) objects.put(object);
      return moved;
    },
  };
}
